/**
 * Key Management Page
 *
 * Allows employees to generate, manage, and backup their encryption keys.
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  ContentCopy as CopyIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  Add as AddIcon,
  Check as CheckIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useCryptoKeyStore, useKeyBackup, StoredKeyPair, OnboardingPayload } from '@antragsmanager/crypto-lib';
import { KeySetupWizard } from '../features/encryption/components/KeySetupWizard/KeySetupWizard';
import { KeyImportDialog } from '../features/encryption/components/KeyImportDialog';

export function KeyManagementPage() {
  const [showWizard, setShowWizard] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showAcceptInvitation, setShowAcceptInvitation] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invitationPayload, setInvitationPayload] = useState('');
  const [isProcessingInvitation, setIsProcessingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [invitationSuccess, setInvitationSuccess] = useState(false);

  const { activeKey, isLoading, error, importOnboarding } = useCryptoKeyStore();
  const { downloadPdf, getKeyJson } = useKeyBackup();

  const formatFingerprint = (fingerprint: string) => {
    const groups: string[] = [];
    for (let i = 0; i < fingerprint.length; i += 4) {
      groups.push(fingerprint.slice(i, i + 4).toUpperCase());
    }
    return groups.join(' ');
  };

  const handleCopyPublicKey = useCallback(async () => {
    if (!activeKey) return;
    try {
      const publicKeyJson = getKeyJson();
      if (publicKeyJson) {
        await navigator.clipboard.writeText(publicKeyJson);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy public key:', err);
    }
  }, [activeKey, getKeyJson]);

  const handleDownloadBackup = useCallback(async () => {
    if (!activeKey) return;
    try {
      await downloadPdf({ ownerName: activeKey.ownerName || 'Benutzer' });
    } catch (err) {
      console.error('Failed to download backup:', err);
    }
  }, [activeKey, downloadPdf]);

  const handleWizardComplete = useCallback((_keyPair: StoredKeyPair) => {
    setShowWizard(false);
  }, []);

  const handleImportComplete = useCallback((_keyPair: StoredKeyPair) => {
    setShowImport(false);
  }, []);

  const handleAcceptInvitation = useCallback(async () => {
    if (!activeKey) {
      setInvitationError('Kein aktiver Schlüssel vorhanden');
      return;
    }

    if (!invitationPayload.trim()) {
      setInvitationError('Bitte fügen Sie die Einladungs-Payload ein');
      return;
    }

    setIsProcessingInvitation(true);
    setInvitationError(null);

    try {
      // Parse the payload
      let payload: OnboardingPayload;
      try {
        payload = JSON.parse(invitationPayload.trim());
      } catch {
        setInvitationError('Ungültiges Payload-Format. Bitte verwenden Sie das JSON-Format.');
        setIsProcessingInvitation(false);
        return;
      }

      // Validate payload structure
      if (!payload.authorityId || !payload.wrappedBundle || !payload.epoch) {
        setInvitationError('Ungültige Onboarding-Payload. Erforderliche Felder fehlen.');
        setIsProcessingInvitation(false);
        return;
      }

      // Import the onboarding payload
      await importOnboarding(payload);

      setInvitationSuccess(true);
    } catch (err) {
      console.error('Failed to accept invitation:', err);
      setInvitationError(
        err instanceof Error
          ? err.message
          : 'Fehler beim Akzeptieren der Einladung. Bitte versuchen Sie es erneut.'
      );
    } finally {
      setIsProcessingInvitation(false);
    }
  }, [activeKey, invitationPayload, importOnboarding]);

  const handleCloseInvitationDialog = useCallback(() => {
    setShowAcceptInvitation(false);
    setInvitationPayload('');
    setInvitationError(null);
    setInvitationSuccess(false);
  }, []);

  // Show wizard if no key exists
  if (!activeKey && !isLoading) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Sie haben noch keinen Verschlüsselungsschlüssel. Erstellen Sie einen neuen Schlüssel
            oder importieren Sie einen vorhandenen.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setShowWizard(true)}
            data-testid="generate-key-button"
          >
            Schlüssel generieren
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<UploadIcon />}
            onClick={() => setShowImport(true)}
          >
            Schlüssel importieren
          </Button>
        </Box>

        {/* Key Setup Wizard Dialog */}
        <Dialog open={showWizard} onClose={() => setShowWizard(false)} maxWidth="sm" fullWidth>
          <KeySetupWizard
            ownerType="CITIZEN"
            onComplete={handleWizardComplete}
            onCancel={() => setShowWizard(false)}
            title="Neuen Schlüssel erstellen"
          />
        </Dialog>

        {/* Import Dialog */}
        <KeyImportDialog
          open={showImport}
          onClose={() => setShowImport(false)}
          onKeyImported={handleImportComplete}
        />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography>Schlüssel wird geladen...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        Fehler beim Laden des Schlüssels: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Schlüsselverwaltung
      </Typography>

      {/* Active Key Card */}
      <Card sx={{ mb: 3 }} data-testid="active-key-card">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <KeyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Aktiver Schlüssel</Typography>
                <Chip
                  label="Aktiv"
                  color="success"
                  size="small"
                  data-testid="active-key-badge"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Public Key kopieren">
                <IconButton onClick={handleCopyPublicKey}>
                  {copied ? <CheckIcon color="success" /> : <CopyIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Backup herunterladen">
                <IconButton onClick={handleDownloadBackup}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Schlüssel-ID:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', mb: 2 }}
              data-testid="key-id"
            >
              {activeKey?.keyId}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Fingerprint:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', mb: 2 }}
              data-testid="fingerprint"
            >
              {activeKey?.fingerprint ? formatFingerprint(activeKey.fingerprint) : '-'}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Eigentümer:
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {activeKey?.ownerName || '-'}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Erstellt am:
            </Typography>
            <Typography variant="body2">
              {activeKey?.createdAt
                ? new Date(activeKey.createdAt).toLocaleString('de-DE')
                : '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Aktionen
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadBackup}
              data-testid="backup-button"
            >
              Backup erstellen
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => setShowExport(true)}
              data-testid="export-button"
            >
              Public Key exportieren
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowAcceptInvitation(true)}
              data-testid="accept-invitation-button"
            >
              Einladung annehmen
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={showExport} onClose={() => setShowExport(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Public Key exportieren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Teilen Sie diesen Public Key mit anderen Mitarbeitern, die Sie zu einer Behörde
            hinzufügen möchten.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={getKeyJson() || ''}
            InputProps={{ readOnly: true }}
            data-testid="export-json"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExport(false)}>Schließen</Button>
          <Button variant="contained" onClick={handleCopyPublicKey}>
            Kopieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accept Invitation Dialog */}
      <Dialog
        open={showAcceptInvitation}
        onClose={handleCloseInvitationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Einladung annehmen</DialogTitle>
        <DialogContent>
          {invitationSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Onboarding erfolgreich! Sie haben jetzt Zugriff auf die verschlüsselten Daten
              der Behörde.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fügen Sie die Onboarding-Payload ein, die Sie von einem bestehenden Mitarbeiter
                erhalten haben. Dies kann aus dem QR-Code oder durch Kopieren der Payload erfolgen.
              </Typography>

              {invitationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {invitationError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Onboarding-Payload (JSON)"
                value={invitationPayload}
                onChange={(e) => setInvitationPayload(e.target.value)}
                margin="normal"
                multiline
                rows={6}
                placeholder='{"authorityId":"...","wrappedBundle":"...","epoch":1,...}'
                helperText="Die Payload enthält den verschlüsselten Zugangsschlüssel für die Behörde"
                data-testid="invitation-payload-input"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvitationDialog}>
            {invitationSuccess ? 'Schließen' : 'Abbrechen'}
          </Button>
          {!invitationSuccess && (
            <Button
              variant="contained"
              onClick={handleAcceptInvitation}
              disabled={isProcessingInvitation || !invitationPayload.trim()}
              data-testid="process-invitation-button"
            >
              {isProcessingInvitation ? <CircularProgress size={24} /> : 'Einladung verarbeiten'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success message after key generation */}
      {activeKey && (
        <Box data-testid="key-generated" sx={{ display: 'none' }}>
          Schlüssel erfolgreich erstellt
        </Box>
      )}
    </Box>
  );
}
